import React from 'react';

/**
 * Catches render-time exceptions from the routed page so one screen throwing
 * (e.g. reading `.length` on a field the API did not return) shows a coded
 * error message instead of unmounting the whole React tree — which left the
 * entire app blank until a full reload, login screen included.
 *
 * Every caught crash gets a reference CODE (never just a blank or a bare
 * message): shown to the operator and POSTed to /api/frontend-error so the same
 * code appears in the server log for support to correlate.
 *
 * Wrapped with a `key` of the current pathname in navigation, so moving to
 * another page remounts the boundary and clears a prior error automatically.
 */

// A short, stable, human-quotable code derived from the route and message, so
// the same crash reads the same on the screen and in the log. Prefix keeps it
// recognisable next to the fetch-error codes the pages already emit.
function deriveCode(routePath, message) {
	const basis = `${routePath || ''}|${message || ''}`;
	let hash = 0;
	for (let i = 0; i < basis.length; i += 1) {
		hash = (hash * 31 + basis.charCodeAt(i)) | 0;
	}
	const hex = Math.abs(hash).toString(16).toUpperCase().padStart(6, '0').slice(0, 6);
	return `WNG-RENDER-${hex}`;
}

class ErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false, error: null, code: null };
	}

	static getDerivedStateFromError(error) {
		return { hasError: true, error };
	}

	componentDidCatch(error, info) {
		const message = (error && error.message) || String(error || 'Unknown error');
		const code = deriveCode(this.props.routePath, message);
		this.setState({ code });

		// eslint-disable-next-line no-console
		console.error(`Page render error [${code}]:`, error, info);

		// Best-effort report to the server log; never let reporting itself throw.
		try {
			if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
				window.fetch('/api/frontend-error', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					keepalive: true,
					body: JSON.stringify({
						code,
						message,
						route: this.props.routePath || null,
						stack: (error && error.stack) || null,
						componentStack: (info && info.componentStack) || null,
						kind: 'render-crash',
					}),
				}).catch(() => {});
			}
		} catch (e) {
			/* reporting is best-effort */
		}
	}

	render() {
		if (this.state.hasError) {
			const message =
				(this.state.error && this.state.error.message) ||
				String(this.state.error || 'Unknown error');
			const code = this.state.code || deriveCode(this.props.routePath, message);
			return (
				<div style={{ padding: '2.5rem', fontFamily: 'sans-serif', color: '#333' }}>
					<h2 style={{ color: '#b02a2a', marginBottom: '0.5rem' }}>
						Something went wrong loading this page.
					</h2>
					<p style={{ marginBottom: '1rem', color: '#666' }}>
						The rest of the app is fine — pick another page from the menu, or reload.
						Quote this code to support:
					</p>
					<div
						style={{
							display: 'inline-block',
							background: '#b02a2a',
							color: '#fff',
							padding: '0.35rem 0.9rem',
							borderRadius: '6px',
							fontFamily: 'monospace',
							fontSize: '1rem',
							marginBottom: '1rem',
						}}
					>
						{code}
					</div>
					<pre
						style={{
							background: '#f4f4f4',
							padding: '0.75rem 1rem',
							borderRadius: '6px',
							overflowX: 'auto',
							fontSize: '0.85rem',
							color: '#a33',
						}}
					>
						{message}
					</pre>
					<button
						type='button'
						onClick={() => window.location.reload()}
						style={{
							marginTop: '1rem',
							padding: '0.5rem 1.25rem',
							background: '#b02a2a',
							color: '#fff',
							border: 'none',
							borderRadius: '6px',
							cursor: 'pointer',
						}}
					>
						Reload
					</button>
				</div>
			);
		}
		return this.props.children;
	}
}

export default ErrorBoundary;
